// New SR modal — create service request form.

import { useState } from "react";
import type { JSX, FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { serviceTypesByCategory, VISIT_TYPES, type ServiceType, type VisitType } from "@hcm/core";
import { apiFetch } from "../lib/api.js";
import { formatPatientRef } from "../lib/refs.js";
import { useAuth } from "../hooks/useAuth.js";
import { Modal } from "./Modal.js";
import { FormField } from "./FormField.js";
import { SelectField } from "./SelectField.js";

// Q7 D7 7b — single dropdown grouped by ServiceCategory via <optgroup>.
// Source of truth lives in @hcm/core; the helper is pure so the result
// is hoisted to module scope rather than memoised per-render.
const SERVICE_TYPE_GROUPS = serviceTypesByCategory();

// Q9 S3 — visit_type is a coordinator-time hint that lets the carer's
// VisitReport wizard skip the step-0 fallback (D4). Optional: leaving it
// blank means the carer picks it themselves at the visit.
const VISIT_TYPE_LABELS: Record<VisitType, string> = {
  standard_wellness:   "Standard wellness",
  extended_care:       "Extended care",
  companion:           "Companion",
  post_discharge:      "Post-discharge",
  emergency_followup:  "Emergency follow-up",
};

interface SrForm {
  patient_id: string;
  service_type: ServiceType;
  visit_type: VisitType | "";
  scheduled_datetime: string;
  special_instructions: string;
  specialist_skill_required: string[];
  agency_id: string;
}

interface PatientItem { _id: string; age: number; city: string; }
interface PatientListResponse { data: PatientItem[]; total: number; page: number; limit: number; }
interface Agency { _id: string; agency_name: string; }

const SPECIALIST_SKILLS = [
  { value: "insulin_administration", label: "Insulin administration" },
  { value: "wound_care",             label: "Wound care" },
  { value: "catheter_care",          label: "Catheter care" },
  { value: "physio_basic",           label: "Physio (basic)" },
  { value: "dementia_trained",       label: "Dementia trained" },
  { value: "post_stroke_care",       label: "Post-stroke care" },
  { value: "palliative_care",        label: "Palliative care" },
];

const INIT: SrForm = {
  patient_id: "", service_type: "elder_care_visit", visit_type: "",
  scheduled_datetime: "", special_instructions: "",
  specialist_skill_required: [], agency_id: "",
};

function usePatientOptions(): ReturnType<typeof useQuery<PatientListResponse>> {
  return useQuery<PatientListResponse>({
    queryKey: ["patients-opts"],
    queryFn: () => apiFetch<PatientListResponse>("/patients?limit=100"),
  });
}

function buildPatientOptions(items: PatientItem[]): { value: string; label: string }[] {
  return [
    { value: "", label: "— select a patient —" },
    ...items.map((p) => ({ value: p._id, label: `${formatPatientRef({ _id: p._id })} · ${p.age}y · ${p.city}` })),
  ];
}

async function submitSr(form: SrForm): Promise<void> {
  const dt = new Date(form.scheduled_datetime).toISOString();
  await apiFetch<unknown>("/sr", {
    method: "POST",
    body: JSON.stringify({
      ...form,
      scheduled_datetime: dt,
      visit_type: form.visit_type || undefined,
      specialist_skill_required: form.specialist_skill_required.length ? form.specialist_skill_required : undefined,
      agency_id: form.agency_id || undefined,
    }),
  });
}

interface Props { onClose: () => void; onCreated: () => void; }

function ServiceTypeSelect({ value, onChange }: { value: ServiceType; onChange: (v: ServiceType) => void }): JSX.Element {
  return (
    <div>
      <label htmlFor="service-type" className="block text-sm font-medium text-slate-700 mb-1">
        Service type<span className="text-red-500 ml-0.5">*</span>
      </label>
      <select
        id="service-type"
        value={value}
        onChange={(e) => onChange(e.target.value as ServiceType)}
        required
        className="w-full rounded-lg border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 border-slate-300 focus:ring-navy-500"
      >
        {SERVICE_TYPE_GROUPS.map((g) => (
          <optgroup key={g.category} label={g.label}>
            {g.types.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

export function NewSrModal({ onClose, onCreated }: Props): JSX.Element {
  const [form, setForm] = useState<SrForm>(INIT);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isNotCarer = user?.role !== "carer";

  const { data: pData } = usePatientOptions();
  const { data: agencyData } = useQuery<{ data: Agency[]; total: number; page: number; limit: number }>({
    queryKey: ["agencies"],
    queryFn: () => apiFetch<{ data: Agency[]; total: number; page: number; limit: number }>("/agencies"),
    enabled: isNotCarer,
  });

  const patientOptions = buildPatientOptions(pData?.data ?? []);
  const agencyOpts = [
    { value: "", label: "No preference" },
    ...(agencyData?.data ?? []).map((a) => ({ value: a._id, label: a.agency_name })),
  ];
  const update = (patch: Partial<SrForm>): void => setForm((prev) => ({ ...prev, ...patch }));

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!form.patient_id) { setError("Please select a patient."); return; }
    setSaving(true); setError(null);
    try { await submitSr(form); onCreated(); }
    catch { setError("Failed to create SR. Check all fields and try again."); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="New Service Request" onClose={onClose}>
      <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className="space-y-3">
        <SelectField label="Patient" value={form.patient_id} onChange={(v) => update({ patient_id: v })} options={patientOptions} required />
        <ServiceTypeSelect value={form.service_type} onChange={(v) => update({ service_type: v })} />
        <SelectField
          label="Visit type"
          value={form.visit_type}
          onChange={(v) => update({ visit_type: v as VisitType | "" })}
          options={[
            { value: "", label: "— Visit type (optional) —" },
            ...VISIT_TYPES.map((v) => ({ value: v, label: VISIT_TYPE_LABELS[v] })),
          ]}
        />
        <FormField label="Scheduled date & time" type="datetime-local" value={form.scheduled_datetime} onChange={(v) => update({ scheduled_datetime: v })} required />
        <FormField label="Special instructions (optional)" value={form.special_instructions} onChange={(v) => update({ special_instructions: v })} />
        <div>
          <p className="block text-sm font-medium text-slate-700 mb-2">Specialist skills required (if any)</p>
          <div className="grid grid-cols-2 gap-1">
            {SPECIALIST_SKILLS.map(({ value, label }) => (
              <label key={value} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.specialist_skill_required.includes(value)}
                  onChange={(e) => update({
                    specialist_skill_required: e.target.checked
                      ? [...form.specialist_skill_required, value]
                      : form.specialist_skill_required.filter((s) => s !== value),
                  })}
                  className="rounded text-navy-700"
                />
                {label}
              </label>
            ))}
          </div>
        </div>
        {isNotCarer && (
          <SelectField label="Assign to agency" value={form.agency_id} onChange={(v) => update({ agency_id: v })} options={agencyOpts} />
        )}
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2">Cancel</button>
          <button type="submit" disabled={saving} className="bg-navy-700 text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-navy-800 disabled:opacity-50 transition-colors">
            {saving ? "Saving…" : "Create SR"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
